<template>
    <div class="base-message-form" :class="'base-message-form-' + threadType">
        <core-error-alerts :errors="errorMsgList"></core-error-alerts>
        <div>
            <b-overlay :show="loading" rounded="md">

                <!-- Title for Comments -->
                <div v-if="threadType === 'comment'">
                    <h6>
                        Add a Comment:
                        <!-- <span class="ml-3 text-muted font-size-80 font-weight-400">Internal Only - Vendors never see comments</span> -->
                    </h6>
                </div>

                <!-- Comment TEXTAREA content editor -->
                <mentionable-custom
                    ref="mentionableComponent"
                    v-if="finalEditorStyle === 'textarea'"
                    :keys="[mentionKey]"
                    :items="tenantUsersFiltered"
                    value-field="id"
                    label-field="full_name"
                    :map-insert="formatForMention"
                    offset="6"
                    placement="top-end"
                    class="mentionable"
                    @open="onMentionableOpen"
                    @mention-object="addTaggedTu"
                >
                    <b-form-textarea
                        id="textarea-comment-body"
                        v-model="comment.body"
                        :placeholder="placeholder"
                        :rows="isEdit ? 8 : 8"
                        class="mb-2"
                        autofocus
                    ></b-form-textarea>
                </mentionable-custom>

                <div class="d-flex" :class="threadType === 'email' ? 'flex-column-reverse' : 'flex-column'">  
                <!-- Flip if its an email to put recipients first -->

                    <!-- Email content editor -->
                    <quill-editor-full 
                        ref="quillEditor"
                        v-if="finalEditorStyle === 'rich'"
                        v-model="comment.body_html" 
                        :placeholder="'Write your ' + commentName + ' here...'" 
                        class="mb-3 overflow-hidden w-100" 
                        wrapper-class=""
                        content-height="300px"
                        toolbar="email"
                    >
                    </quill-editor-full>
                    
                    <!-- Recipients -->
                    <div class="mb-2">
                        <div v-if="hideMembers">  <!-- If not showing recipients, add this title -->
                            <h6>Send Message:</h6>  
                        </div>
                        <div
                            v-else
                            class="d-flex align-items-center"
                        >
                            <div 
                                v-if="threadType === 'email'"
                                class="d-flex align-items-center">
                                <div 
                                    class="small font-weight-bold text-uppercase text-muted text-right mb-0 mr-2" 
                                    style="white-space: nowrap;"
                                >
                                    Recipients
                                </div>
                                <div>
                                    <base-popover-icon
                                        title="Recipients"
                                        :content="'These people will receive your ' + commentName + ' by email.  Make changes by modifying the Participants on the message thread.'"
                                    ></base-popover-icon>
                                </div>
                            </div>
                            <div 
                                v-if="threadType === 'comment'"
                                class="d-flex align-items-center">
                                <div class="small font-weight-bold text-uppercase text-muted text-right mb-0 mr-2" style="white-space: nowrap;">Who will get this?</div>
                                <div>
                                    <base-popover-icon
                                        title="Who will get this?"
                                        :content="'All Followers will be notified of your ' + commentName + ' by email, as well as anyone you @mention in the ' + commentName + '.'"
                                    ></base-popover-icon>
                                </div>
                            </div>
                            <div class="flex-fill">
                                <div class="d-flex justify-content-start align-items-center flex-wrap">
                                    <div v-for="notifyTu in taggedOrNotifyTenantUsers" :key="notifyTu.id" class="ml-2 my-1">
                                        <tenant-user-badge 
                                            :tu-data="notifyTu" 
                                            :deselectable="isDeselectable(notifyTu.id)" 
                                            @deselect="deselectTaggedTu"
                                        ></tenant-user-badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Tagged TUs for comments -->
                <!-- <div
                    v-if="threadType === 'comment'"
                    id="comment-collaborator-tags" 
                    class="flex-grow-1 d-flex align-items-center mb-2"
                >
                    <div class="flex-fill">
                        <div
                            class="d-flex align-items-center"
                        >
                            <multiselect 
                                v-model="comment.taggedTenantUsers" 
                                tag-placeholder="Add new team member" 
                                placeholder="Tag or @mention team members to notify them of your comment..." 
                                label="full_name" 
                                track-by="id" 
                                :options="tenantUsersFiltered" 
                                :multiple="true" 
                                style=""
                                class="flex-fill"
                                @remove="removeTaggedTuFromBody"
                            >
                                <span slot="noOptions">No team members to add...</span>
                            </multiselect>
                        </div>
                    </div>
                </div> -->

                <!-- Files / Attachments -->
                <div>
                    <b-collapse v-if="!noFiles" id="file-upload-area-collapse" v-model="fileAreaVisible" class="my-3">
                        <div v-if="isEdit && hasExistingFiles">
                            <h6>
                                Attachments:
                            </h6>
                            <div class="">
                                <div v-for="file in comment.existingFiles" :key="file.id" class="mr-2">
                                    <file-download-badge :file-data="file"></file-download-badge>
                                    <b-button variant="minimal-danger" class="ml-2" @click="removeExistingFile(file)">
                                        <b-icon icon="x"></b-icon>
                                        Remove
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        <!-- <formulate-input
                            type="s3fileuploader"
                            name="comment_s3files"
                            v-model="comment.s3files"
                            label="Select file(s) to upload, or drag-and-drop below:"
                            file-category="messaging"
                            :parent-id="''"
                            :max-total-filesize="(10 * 1024 * 1024)"
                            :no-preview="true"
                        ></formulate-input> -->
                        <formulate-input
                            v-show="!(isEdit && hasExistingFiles)"
                            type="file"
                            name="comment_attachment"
                            v-model="comment.attachment"
                            label="Select file(s) to upload, or drag-and-drop below:"
                            :uploader="formulateUploader"
                            upload-behavior="live"
                            multiple
                        ></formulate-input>  <!-- @input="setUploadedFiles" -->  <!-- :upload-area-mask-class="['bg-gray-100']" -->
                        <!-- :element-class="['w-100', 'mw-100']" -->
                    </b-collapse>
                </div>
                <!-- <hr> -->
                <!-- Bottom Buttons -->
                <div class="d-flex justify-content-between mt-2">
                    <div>
                        <b-button 
                            v-if="!noFiles && !hasFiles" 
                            variant="gray-secondary"
                            :class="fileAreaVisible ? null : 'collapsed'"
                            :aria-expanded="fileAreaVisible ? 'true' : 'false'"
                            aria-controls="file-upload-area-collapse"
                            @click="fileAreaVisible = !fileAreaVisible"
                        >
                            <b-icon icon="paperclip" class="mr-2"></b-icon>
                            <span class="text-uppercase small">{{ fileAreaVisible ? 'Cancel' : 'Attach File' }}</span>
                        </b-button>
                    </div>
                    <div>
                        <b-button
                            variant="light-secondary"
                            class="mr-2"
                            @click="emitCancel"
                        >Cancel</b-button>
                        <b-button
                            variant="primary"
                            @click="isEdit ? saveEditComment() : postMessage()"
                            :disabled="!allInputsValid"
                        >
                            {{ isEdit ? 'Update' : submitVerb }}&nbsp;{{commentName | capitalize}}
                        </b-button>
                        <!-- <span v-if="uploadPending">Upload Pending</span> -->
                        <!-- <b-button 
                            v-if="!noFiles && !(isEdit && hasExistingFiles)" 
                            variant="gray-secondary"
                            :class="fileAreaVisible ? null : 'collapsed'"
                            :aria-expanded="fileAreaVisible ? 'true' : 'false'"
                            aria-controls="file-upload-area-collapse"
                            @click="fileAreaVisible = !fileAreaVisible"
                        >
                            <b-icon icon="paperclip" class="mr-2"></b-icon>
                            <span class="text-uppercase small">Attach File</span>
                        </b-button> -->
                    </div>


                </div>
            </b-overlay>
        </div>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import isEmpty from 'lodash/isEmpty';
//import Multiselect from 'vue-multiselect';
//import { Mentionable } from 'vue-mention';
import MentionableCustom from '../MentionableCustom';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import unionBy from 'lodash/unionBy';
import { asyncSplFileSlinger } from '../../js/file-helpers';
import { currentUserInfoMixin } from '../../js/custom-vue-mixins';
import { FormatCapitalizeHelper } from '../../js/filters-helpers';

/*
for 'comment' types:
- show 'tagged people' list / multiselect to tag people without @mentions and remove people already @mentioned
- tagged people and senders will get notify=True after posting the message - so any email responses will get sent to them
- for new comments from the app, commenters can un-select people to notify
    - therefore... when opening 'new comment' form in the app, populate tagged TUs with TUs who have notify=True
- summary:
    - new comments via inbound email:  send to all with notify=True since there is no way to tag people, just assume all notify=True are tagged
    - new comments via app:  send ONLY to tagged TUs, so people can remove people to notify if they want to post a non-important comment
        - removing someone from tagged TUs in a comment does not affect their notify status, but they won't get notification of that message
        - adding someone to tagged TUs in a comment SHOULD make them notify = True so they get any follow-up comment (think about more than 2 people on the thread)
            - but they can always unsubscribe
        - or... just have one checkbox to "don't send notifications about this" which removes everyone from tagged
            - this makes more sense, because if someone removes only one person from tagged TUs, but leaves others, the person they removed will get any email reply, which may be misleading
            - YES... THIS IS BEST... better than allowing individual un-tagging... actually no... too confusing
        - so... messages sent from app should not notify anyone by default... let author tag people
            - tagged people get notify = True after they are tagged
            - authors get notify=True after sending
            - only add RFX event admin with notify=True by default... for non-RFX things and ohter RFX users, add with write access, but notify=False
            - replied emails go to anyone with notify=True, but those will very likely be people who really should be notified (previously an author, or tagged on the thread), and mention the list of TUs who will receive email responses
            - this is SO much easier than sending to notify=True, but giving option to add more and option to notify none (that's too complex, both UI and mentally)
            - but actually, the basecamp way is also not bad, very straightforward mentally (users can decide what they want to follow)
            - CONCLUSION:  follow the basecamp way
                - start with only the event owner with notify=True, all other internal people with notify=False
                - when someone is tagged in a comment, give them notify=True so any group messages go to them also
                - when someone sends a comment, give them notify=True, so any replies go to them also
                - must allow people to 'unsubscribe' selves from thread (with link in email, and in app)
    - when adding TUs to thread (membership) in the first place, give admins (and maybe full members) notify = True
    - let users set notify = False (unsubscribe) for themselves in which case they'll only get notification if tagged in the app
    - basically, this is just like Basecamp comment logic, but with ability to un-notify people (by removing them from tagged) for comments sent from app
    - for email notifications, list all the users who will get notified if a reply (with note for 'plus anyone else added to the thread since this email was sent')

for 'email' types:
- no tagging of people when creating emails in the app -- becuase its too hard to manage recipient list for individual messages (see above... same reason you're not going to do individual un-tagging for comments)
- fixed list of recipients -- based on who has notify=True - want to change it?  then change it for the entire thread so any email replies go to the same recipients
- this lets you use same notification-sending logic for 'comment' and 'email' types
    - any tagged TUs get notify=True just after save, then always send to people who have notify=True (including newly tagged TUs)
    - for emails, you just won't have any tagged TUs, and you'll start with more people with notify=True
    - for comments, you'll have less people starting with notify=True, but you'll sometimes have tagged TUs to deal with 
- who gets notify=True from the start on emails?  only admin?  
    - in emails, there's no way to 'tag' someone, or add them as a recipient, other than manually changing who is notified... so should we add more people from the start?
    - doesn't really make sense to add collaborators to vendor email thread, but NOT add them to comment thread... confusing.  should be consistent.
    - should probably make a way to 'tag' internal people in emails (ie: add them to recipient list)...
    - ... or just use thread checkboxes... that keeps everything in one place and consistent
    - by default, add EXTERNAL people with notify = True
    - by default, add INTERNAL collaborators with notify = False 
    - by default, add EVENT ADMINS with notify = True

*/

export default {
    name: 'BaseMessageForm',
    components: {
        //Multiselect,
        MentionableCustom,
    },
    mixins: [
        currentUserInfoMixin,
        errorHandling,
    ],
    props: {
        threadId: {  // MessageThread ID (likely UUID)
            type: [String, Number],
            required: true,
        },
        threadType: {  // a field on MessageThread model -- 'comment' or 'email'
            type: String,
            default: 'comment',
        },
        // editorStyle is just the editor type to use, independent of threadType... for comments, we could use either editorStyle
        editorStyle: {  // can be 'textarea' or 'rich' -- 'textarea' will show @mentionable textarea, 'rich' will show quill rich text editor
            type: String,
            default: 'textarea'
        },
        placeholder: {
            type: String,
            default: 'Write your comment, @mention your team members...',
        },
        editComment: {  // if editing existing comment, pass it in here
            type: Object,
            default: function() {
                return {};
            },
        },
        tenantUsers: {  // array of TenantUser objects, for tagging them, if not provided, tagging will be disabled
            type: Array,
            required: false,
        },
        notifyTenantUsers: {  // default set of TenantUsers who will be notified (have notify=True on the thread)
            type: Array,
            required: false,
        },
        noFiles: {  // if set, we hide all file attachment stuff and don't send 'files' field in post data
            type: Boolean,
            default: false,
        },
        createVerb: {  // verb to use for creating a new thing -- 'Add' or 'Send' or something like that
            type: String,
            default: 'Add',
        },
        commentName: {
            type: String,
            default: 'comment',
        },
        hideMembers: {  // if true, we will hide recipients (for both email and comment types)
            type: Boolean,
            default: false,
        },
        disableMentions: {  // set to true if you want to disable @mentions, only used when using mentionable textarea component
            type: Boolean,
            default: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            comment: {},  // this is the current comment we're editing... we make it / set it / fill it in the created hook, depending on if new or edit
            attachments: false,  // v-model to the file uploads  (maybe not... not using Dec 3 2022)
            blankComment: {
                id: null,  // a placeholder for existing ID for when we're editing
                body: '',  // the v-model with the mentionable textarea
                body_html: '',  // rich-text HTML content version goes here
                existingFiles: [],  // if any EXISTING files are on the comment (like in edit mode) put them here
                // we'll display them separately from the vue-formulate input, since vue-formulate doesn't display things nicely when AWS S3 querystring is attached, and has bug for removing existing files
                // existingFiles will be in our serializer format for s3 files (array of objects from our serializer)... not same format as vue-formulate files
                attachment: null,  // the v-model with the vue-formulate file input -- will be JS File object (or array of them if you allow multiple)
                s3files: null,  // new v-model for s3fileuploader - keeping separate from original one, but should only have one... then you extract IDs before posting data
                taggedTenantUsers: [],  // full objects of tagged TenantUser objects -- the v-model with the multi-select
            },
            fileAreaVisible: false,  // v-model to file area collapse
            //uploadedFiles: null,  // update this via @input event of vue-formulate file input
            isEdit: false,  // we'll set this in created hook
            //mentionKey: '',  // key to use for comment mention tagging  (you pass it in as array in the template)
            uploadPending: false,  // flag we set to true when file is uploading
            modifyNotify: false,  // local toggle for showing multiselect for modifying who is notified
            htmlContent: '',  // Quill content
        }
    },
    filters: {
        capitalize: function(value) {
            return FormatCapitalizeHelper(value);
        },
    },
    computed: {
        /*
        isEdit: function() {  // return true if editing existing comment, false if new comment
            return !isEmpty(this.editComment);
        },*/
        // has currentUserInfo from mixin
        finalEditorStyle: function() {
            // return 'rich' or 'textarea' based on threadType and editorStyle props
            if (this.threadType === 'comment') {
                return this.editorStyle;  // comments can use either type
            }
            if (this.threadType === 'email') {
                return 'rich';  // emails can only use 'rich'
            }
            return 'textarea';  // fallback default
        },
        mentionKey: function() {
            // return a STRING of character to use in vue Mentionable component -- normally just '@'
            // but return empty string if we want to disable tagging / @mentions -- this is how we disable it
            if (this.disableMentions) return '';
            return '@';
        },
        hasTenantUsers: function() {  // returns True if we have list of TenantUsers to tag
            return Array.isArray(this.tenantUsers) && this.tenantUsers.length > 0;
        },
        tenantUsersFiltered: function() {  // filter out current user
            if (!this.hasTenantUsers) {
                return [];
            }
            return this.tenantUsers.filter(tu => tu.id != this.currentUserInfo.tenant_user_id);
        },
        taggedTenantUserIds: function() {
            if (!this.comment.taggedTenantUsers || !this.comment.taggedTenantUsers.length) {
                return [];
            }
            return this.comment.taggedTenantUsers.map(tu => tu.id);
        },
        taggedOrNotifyTenantUsers: function() {
            // one MERGED LIST OF TU OBJECTS -- combining any from comment.taggedTenantUsers AND from notifyTenantUsers
            // after saving message to DB, the tagged TUs will get added to notify=True so they will get any reply, but only on backend
            // this is what we actually use to show list of recipients
            let taggedTus = (this.comment.taggedTenantUsers && this.comment.taggedTenantUsers.length) ? cloneDeep(this.comment.taggedTenantUsers) : [];
            let notifyTus = (this.notifyTenantUsers && this.notifyTenantUsers.length) ? cloneDeep(this.notifyTenantUsers) : [];
            return unionBy(taggedTus, notifyTus, 'id');
        },
        hasExistingFiles: function() {  // boolean to tell us if there are existing files... these will be in our own serializer format, different format than vue-formulate attachment.  if any, the files will be passed in during edit mode
            if (this.comment.existingFiles && this.comment.existingFiles.length) return true;
            return false;
        },
        hasNewFiles: function() {
            // return true if we have any newly uploaded files
            return this.attachmentIds && this.attachmentIds.length;
        },
        hasFiles: function() {
            // return true if there are any uploaded files, newly uploaded or existing
            return this.hasNewFiles || this.hasExistingFiles;
        },
        attachmentIds: function() {
            // gets a list of UUIDs for uploaded files (under data.comment.attachment) assuming v-model from vue-formulate file component
            // we're allowing multiple uploads in this one field, so results will be an array
            // DEC 3 2022 - changed to use s3fileuploader component - using different key in data for now
            if (this.comment.s3files && Array.isArray(this.comment.s3files)) {
                return this.comment.s3files.map(f => f.id);
            }
            if (!this.comment.attachment) return [];  // if we didn't start the upload process, attachment will be null
            if (!this.comment.attachment.results) return [];  // attachment results will be array of objects, each object has keys of id: and url:, as returned from our backend if upload process was done
            return this.comment.attachment.results.map(file => file.id);  // return an array of file uploaded IDs (uuids)
        },
        /* attachmentFiles: function() {  // a list of files already uploaded... used to show already uploaded files during EDIT MODE until vue-formulate handling of existing files can be fixed
            // MAYBE NO LONGER NEEDED
            if (!this.comment.attachment) return [];  // if we didn't start the upload process, attachment will be null
            if (!this.comment.attachment.files) return [];  // attachment files will be array of objects
            return this.comment.attachment.files;  // return an array files, full object
        }, */
        dataForApi: function() {
            // put things into format/structure that you want for API
            // you only need body, tagged_tenantusers (IDs), files (IDs)
            // for create, threadId will be set in the view  (using @action on thread endpoint)
            // for edit, message ID will be set based on the URL endpoint
            let apiObj = {};
            apiObj['body'] = this.comment.body;
            apiObj['body_html'] = this.comment.body_html;
            if (this.hasTenantUsers) {
                apiObj['tagged_tenantusers'] = [...this.taggedTenantUserIds];  // spread operator to copy the array of IDs
            }
            if (!this.noFiles) {
                // add files UUIDs (already uploaded!) to 'files' field, unless we specifically disabled this, then don't add this at all to the post data
                // this should be an array of file UUIDs, UUIDs should already be saved to your django DB
                // if in edit mode, we have to check comment.existingFiles -- that's where we put any files previously attached
                // if in create OR edit mode, we have to check comment.attachment for any new uploads... in edit mode it should be one or the other.... not both
                let existingFileIds = this.comment.existingFiles.length ? this.comment.existingFiles.map(file => file.id) : [];
                let newFileIds = [...this.attachmentIds];
                apiObj['files'] = [...newFileIds, ...existingFileIds];
            }
            return apiObj;
        },
        allInputsValid: function() {
            // if you add more inputs, make sure to check validity here
            // TODO - more here?  other things may be blank, but maybe there are other invalid possibilities?
            let hasContent = !!this.comment.body || !!this.comment.body_html;
            let uploadPendingOk = !this.uploadPending;  // make sure there isn't an upload in process
            return hasContent && uploadPendingOk;
            // add one to make sure file isn't in process of uploading
        },
        editCommentId: function() {
            // the comment object passed in might have .id or .pk... this checks both
            if (!this.isEdit) {
                return null;
            }
            if (this.editComment.id) {
                return this.editComment.id;
            } else {
                return this.editComment.pk ? this.editComment.pk : null;
            }
        },
        submitVerb: function() {
            // something like 'Send' or 'Add' or 'Post' -- different than 'createVerb' prop
            if (this.threadType === 'comment') return 'Submit';
            if (this.threadType === 'email') return 'Send';
            return 'Submit';  // fallback value
        },
    },
    created: function() {
        this.resetInputs();  // this does cloneDeep on blankComment to comment then modify depending on new/edit

        let isEdit = false;  // start with this...
        if (!isEmpty(this.editComment)) {  // check if editComment has anything... if so, we'll consider this an edit
            isEdit = true;  // set a local variable to make things easy
            this.isEdit = isEdit;  // and set the overall instance variable
        }

        if (isEdit) {  // if this is EDIT of EXISTING comment/message - set our local input values
            this.comment.id = this.editComment.id || null;
            this.comment.body = this.editComment.body || '';
            // TODO - need to fix this -- does not retain HTML markup, so loses formatting when putting back in Quill editor
            // should always save it as HTML, then dump out as plain text if/when needed
            // MUST MAKE SURE TO BLEACH ALSO
            this.comment.body_html = this.editComment.body || '';  
            let incomingTaggedTuIds = this.editComment.tagged_tenantusers ? this.editComment.tagged_tenantusers.map(tu => tu.id) : [];  // make a list of IDs of the pre-tagged TUs... do it this way in case TU object format is different than what multiselect uses
            this.comment.taggedTenantUsers = this.tenantUsers.filter(tu => incomingTaggedTuIds.includes(tu.id));  // get the list to set on v-model based on filtering tenantUsers list by IDs of ones already tagged
            if (this.editComment.files && this.editComment.files.length) {
                this.comment.existingFiles = cloneDeep(this.editComment.files);
            }
            this.fileAreaVisible = (this.editComment.files && this.editComment.files.length) ? true : false;  // expand the files area if we have files
        } else {  // handle the NEW COMMENT / MESSAGE case
            // OLD -- we were setting taggedTenantUsers to be the current TUs with notify=True on the thread.... but no longer doing that
            // now we will only 'tag' the TUs we actually tag, then add them to notify=True on the backend
            // OLD:  this.comment.taggedTenantUsers = cloneDeep(this.notifyTenantUsers);
        }
    },
    mounted: function() {
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorMsgList - output via core-error-alerts component
        postMessage: function() {
            // creating new comment will go to different endpoint, perhaps with different overall response format, so we split create and edit
            console.log('hello from postMessage method');
            this.loading = true;
            //let dataToPost = cloneDeep(this.dataForApi);  // grab our data
            let dataToPost = this.getDataForApi();
            /* let msgEndpointData = cloneDeep(dataToPost);
            msgEndpointData['thread'] = this.threadId;
            msgEndpointData['source'] = 'app'; */
            return suppliosApi.messageThread.postNewMessage(this.threadId, dataToPost)
            //return suppliosApi.coreMessage.createWithFullResponse(msgEndpointData)
                .then(response => {
                    console.log('posted comment, full response below');
                    console.log(response);
                    // response should send back comment in same format as in comment list serializer so we can emit it here and push directly to comment array in other component
                    // comment list component can then either refresh entire list, or push new comment to its array directly
                    this.$emit('created', response.data);
                    this.resetInputs();
                    return true;
                })
                .catch(error => {
                    //this.handleError(error, 'Error while posting new comment');
                    this.apiErrorHandler(error, 'Error while sending your message', true)
                    return false;
                })
                .then(status => {
                    this.loading = false;
                    return status;
                })
        },
        resetInputs: function() {
            this.comment = cloneDeep(this.blankComment);
        },
        emitCancel: function() {
            this.$emit('cancel');
        },
        getDataForApi: function() {
            // grab a copy of the current data formatted for API
            let dataToPost = cloneDeep(this.dataForApi); 
            // here is where we populate body <-> body_html based on what we have, and what is missing
            if (this.finalEditorStyle === 'rich') {  // if we only had rich text (body_html) get the plain text body
                let plainTextContent = this.$refs.quillEditor.getText();
                console.log('plainTextContent');
                console.log(plainTextContent);
                // we are NOT checking if there's existing content to overwrite, because we WANT to overwrite in the case of edits/updates
                dataToPost['body'] = plainTextContent || '';
            }
            // now if we were using 'textarea' editor, need to populate body_html -- just copy the plain text
            if (this.finalEditorStyle === 'textarea') {
                // we are NOT checking if there's existing content to overwrite, because we WANT to overwrite in the case of edits/updates
                dataToPost['body_html'] = dataToPost['body'] || '';
            }
            console.log('here is dataToPost');
            console.log(dataToPost);
            return dataToPost;
        },
        saveEditComment: function() {
            console.log('hello from saveEditComment method');
            this.loading = true;
            let dataToPost = this.getDataForApi();
            suppliosApi.coreMessage.partialUpdate(this.editCommentId, dataToPost)
                .then(response => {
                    console.log('posted edited comment, full response below');
                    console.log(response);
                    this.$emit('updated', response.data);
                    this.resetInputs();
                })
                .catch(error => this.apiErrorHandler(error, 'Error while sending your message', true))
                .then(() => this.loading = false)
        },
        onMentionableOpen: function() {
            /* let mentionableComp = this.$refs.mentionableComponent;
            console.log("Here is our mentionable component's $el:");
            console.log(mentionableComp.$el);
            let mentionPopover = this.$refs.mentionableComponent.$refs.popper.$refs.popover;
            console.log("Here is our mentionable popover and its $el:");
            console.log(mentionPopover);
            console.log("and here is the popovers current styles:");
            console.log(JSON.stringify(mentionPopover.style));
            console.log(mentionPopover.style); */
        },
        formatForMention: function(tuObj) {
            // this is a function that you pass in to MentionableCustom for mapInsert, and also used removeTaggedTuFromBody
            // important that they both use the same thing
            // takes in entire object in the same format that you pass into MentionableCustom :items prop, and same format you pass into multiselect
            return tuObj.full_name.split(' ').join('');  // takes out spaces in the users name
        },
        addTaggedTu: function(mentionedObject) {
            // called when we actually tag someone using @mention
            let newMentionObj = cloneDeep(mentionedObject);
            let taggedList = cloneDeep(this.comment.taggedTenantUsers);
            taggedList.push(newMentionObj);  // we may have added a duplicate
            // now use lodash to make this a unique list using isEqual as comparator, and it returns a new array
            this.comment.taggedTenantUsers = uniqWith(taggedList, isEqual);
        },
        removeTaggedTu: function(tuObj) {
            // remove the TU from comment.taggedTenantUsers -- not needed if doing it from multiselect, since that does it itself
            let originalTagged = cloneDeep(this.comment.taggedTenantUsers);
            // now remove the tuObj using filter with !== condition
            this.comment.taggedTenantUsers = originalTagged.filter(tu => tu.id !== tuObj.id);
        },
        removeTaggedTuFromBody: function(tagObj) {
            // you'll get the entire object of TU being removed... then you have to search through text to see if string is there and take it out
            let originalCommentBody = this.comment.body;
            let mentionToRemove = this.mentionKey + this.formatForMention(tagObj);  // this should be something like @FullName
            this.comment.body = originalCommentBody.replace(mentionToRemove,'');
        },
        formulateUploader: function(file, progress, error, option) {
            // pass this function to 'uploader' prop of formulate-input 
            // must return a Promise that resolves to array of objects, at least with 'url' key in each object (but you also expect 'id' for your own backend handling)
            // and must accept 4 args (file, progress, error, option)
            this.uploadPending = true;  // set our flag to true so we can block things while uploading
            let file_category = 'messaging';
            let uploadResponse = asyncSplFileSlinger(file_category, file, progress, error, option);
            let finalReturn = uploadResponse.then(resp => {
                this.uploadPending = false;
                return resp;
            });
            return finalReturn;
        },
        /* setUploadedFiles: function(payload) {
            this.uploadedFiles = payload;
        }, */
        removeExistingFile: function(fileObj) {
            // remove an existing file -- only applies during edit mode to remove something from comment.existingFiles, not for removing just-uploaded file
            let fileIdToRemove = fileObj.id;
            let indexToRemove = this.comment.existingFiles.findIndex(file => file.id === fileIdToRemove);
            this.comment.existingFiles.splice(indexToRemove, 1);  // delete 1 item from array starting at indexToRemove -- mutates the array
        },
        isDeselectable: function(tuId) {
            // pass in the ID for a TenantUser (a tagged one) this returns boolean whether we can deselect that TU
            // will be false if that TU is a notifyTu, true if they are not a notifyTu
            let notifyTuIds = (this.notifyTenantUsers && this.notifyTenantUsers.length) ? this.notifyTenantUsers.map(tu => tu.id) : [];
            return !notifyTuIds.includes(tuId);
        },
        deselectTaggedTu: function(tuObj) {
            // 2-step process... remove TU from comment.taggedTenantUsers, then remove the @mention from body
            this.removeTaggedTu(tuObj);
            this.removeTaggedTuFromBody(tuObj);
        },
    }
}
</script>

<style lang="scss">

.base-message-form {

    .ql-toolbar {
        background-color: var(--gray-100);
    }
    .ql-editor {
        background-color: #fff;
    }

    #comment-collaborator-tags {
        .multiselect__tag {
            background-color: var(--primary-100);
            color: var(--primary-800);
        }
        .multiselect__tag-icon {
            color: rgba(255,255,255,0.5);
        }
        .multiselect__tag-icon:hover {
            background-color: var(--primary-200);
        }
        .multiselect__option--highlight,
        .multiselect__option--highlight::after {
            background-color: var(--primary-400);
        }
        .multiselect__option--selected,
        .multiselect__option--selected::after {
            color: #fff;
            background-color: var(--info-400);
        }
        .multiselect__input,
        .multiselect__option {
            font-size: 14px;
        }
    }
}

/* #comment-collaborator-tags .multiselect__tag {
    background-color: var(--primary-100);
    color: var(--primary-800);
}
#comment-collaborator-tags .multiselect__tag-icon {
    color: rgba(255,255,255,0.5);
}
#comment-collaborator-tags .multiselect__tag-icon:hover {
    background-color: var(--primary-200);
}
#comment-collaborator-tags .multiselect__option--highlight,
#comment-collaborator-tags .multiselect__option--highlight::after {
    background-color: var(--primary-400);
}
#comment-collaborator-tags .multiselect__option--selected,
#comment-collaborator-tags .multiselect__option--selected::after {
    color: #fff;
    background-color: var(--info-400);
}
#comment-collaborator-tags .multiselect__input,
#comment-collaborator-tags .multiselect__option {
    font-size: 14px;
} */

</style>